import React, { useState, useMemo, useCallback } from "react";
import { auth, firestore } from "../../../../firebaseConfig";
import { doc, setDoc, collection, writeBatch, getDoc, getDocs } from "firebase/firestore";
import toast from "react-hot-toast";
import { jsPDF } from "jspdf";
import {
  CheckSquare,
  Square,
  Users,
  Printer,
  AlertCircle,
  Hash,
  Loader2,
} from "lucide-react";
import { ROWS_PER_PAGE, PAPER_TYPES, PDF_LAYOUT, DAYS_OF_WEEK, PAPER_ID_LENGTH } from "./constants";
import { generateUniqueId } from "./utils";

const GeneratePapersTab = ({ companyName, farmers, canGenerate, userRole }) => {
  const [selectedFarmers, setSelectedFarmers] = useState([]);
  const [paperType, setPaperType] = useState(PAPER_TYPES.WEEKLY);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedHalf, setSelectedHalf] = useState("first"); // "first" (1-15) or "second" (16-30/31)
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [paperIdLookup, setPaperIdLookup] = useState("");
  const [paperLookupLoading, setPaperLookupLoading] = useState(false);

  // Filter farmers based on search term
  const filteredFarmers = useMemo(() => {
    if (!searchTerm.trim()) return farmers;
    const lower = searchTerm.toLowerCase();
    return farmers.filter(
      (f) =>
        (f.cloudName && f.cloudName.toLowerCase().includes(lower)) ||
        (f.address && f.address.toLowerCase().includes(lower)) ||
        (f.otherName && f.otherName.toLowerCase().includes(lower))
    );
  }, [farmers, searchTerm]);

  // Toggle farmer selection
  const toggleFarmerSelection = useCallback((farmerId) => {
    setSelectedFarmers((prev) => {
      if (prev.includes(farmerId)) {
        return prev.filter((id) => id !== farmerId);
      }
      return [...prev, farmerId];
    });
  }, []);

  // Select all filtered farmers
  const selectAllFarmers = useCallback(() => {
    setSelectedFarmers(filteredFarmers.map((f) => f.id));
  }, [filteredFarmers]);

  // Deselect all farmers
  const deselectAllFarmers = useCallback(() => {
    setSelectedFarmers([]);
  }, []);

  const loadFarmersFromPaper = async () => {
    const trimmedId = paperIdLookup.trim();
    if (!trimmedId) {
      toast.error("Enter a paper ID to reuse selections");
      return;
    }

    setPaperLookupLoading(true);

    try {
      const paperRef = doc(firestore, "milk_journals", trimmedId);
      const snap = await getDoc(paperRef);

      if (!snap.exists()) {
        toast.error("Paper not found. Please check the ID.");
        return;
      }

      const data = snap.data();
      if (data.companyName !== companyName) {
        toast.error("This paper belongs to a different company");
        return;
      }

      const mappingsSnap = await getDocs(collection(paperRef, "farmer_mappings"));
      if (mappingsSnap.empty) {
        toast.error("No farmer mappings found for this paper");
        return;
      }

      const mappings = mappingsSnap.docs
        .map((d) => d.data() || {})
        .filter((m) => m.farmerId);

      const sortedMappings = mappings.sort((a, b) => {
        const pageDiff = (a.pageNumber || 0) - (b.pageNumber || 0);
        if (pageDiff !== 0) return pageDiff;
        return (a.rowId || 0) - (b.rowId || 0);
      });

      const orderedIds = [];
      const seen = new Set();
      sortedMappings.forEach((m) => {
        if (farmers.some((f) => f.id === m.farmerId) && !seen.has(m.farmerId)) {
          seen.add(m.farmerId);
          orderedIds.push(m.farmerId);
        }
      });

      if (orderedIds.length === 0) {
        toast.error("None of the mapped farmers are available in your list");
        return;
      }

      // Align paper settings where possible
      if (data.paperType && Object.values(PAPER_TYPES).includes(data.paperType)) {
        setPaperType(data.paperType);
      }
      if (data.dateRange?.toString().includes("16-end")) {
        setSelectedHalf("second");
      } else if (data.dateRange?.toString().includes("1-15")) {
        setSelectedHalf("first");
      }

      setSelectedFarmers(orderedIds);
      toast.success(`Loaded ${orderedIds.length} farmers from paper ${trimmedId}`);
    } catch (error) {
      console.error("Error loading paper farmers:", error);
      toast.error("Failed to load farmers from paper ID");
    } finally {
      setPaperLookupLoading(false);
    }
  };

  // Get date headers based on paper type
  const getDateHeaders = () => {
    if (paperType === PAPER_TYPES.SINGLE_DAY) {
      if (!selectedDate) return [];
      const date = new Date(selectedDate);
      return [date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })];
    } else if (paperType === PAPER_TYPES.WEEKLY) {
      // Generate Saturday to Friday dates
      if (!selectedDate) return DAYS_OF_WEEK;
      const startDate = new Date(selectedDate);
      // Adjust to Saturday
      const dayOfWeek = startDate.getDay();
      const daysToSaturday = dayOfWeek === 0 ? -1 : 6 - dayOfWeek;
      startDate.setDate(startDate.getDate() + daysToSaturday);

      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        return date.toLocaleDateString("en-US", { weekday: "short", day: "numeric" });
      });
    } else if (paperType === PAPER_TYPES.HALF_MONTHLY) {
      if (!selectedDate) {
        return selectedHalf === "first"
          ? Array.from({ length: 15 }, (_, i) => `${i + 1}`)
          : Array.from({ length: 15 }, (_, i) => `${i + 16}`);
      }
      const date = new Date(selectedDate);
      const year = date.getFullYear();
      const month = date.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      if (selectedHalf === "first") {
        return Array.from({ length: 15 }, (_, i) => `${i + 1}`);
      } else {
        // Second half: from 16 to end of month (28-31 depending on month)
        const startDay = 16;
        const endDay = daysInMonth;
        return Array.from({ length: endDay - startDay + 1 }, (_, i) => `${startDay + i}`);
      }
    }
    return [];
  };

  // Generate PDF papers
  const generatePDF = async () => {
    // Preserve selection order: map selectedFarmers to farmer data in order of selection
    const selectedFarmerData = selectedFarmers
      .map((id) => farmers.find((f) => f.id === id))
      .filter(Boolean);
    if (selectedFarmerData.length === 0) {
      toast.error("Please select at least one farmer");
      return;
    }

    // Date is required for all paper types
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    setGenerating(true);

    try {
      const dateHeaders = getDateHeaders();
      const uniqueId = generateUniqueId();

      // Create PDF
      const pdfDoc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      // Calculate number of pages needed
      const totalPages = Math.ceil(selectedFarmerData.length / ROWS_PER_PAGE);

      // Store paper metadata in Firestore
      const paperMetadata = {
        uniqueId,
        companyName,
        paperType,
        dateRange: paperType === PAPER_TYPES.SINGLE_DAY
          ? selectedDate
          : paperType === PAPER_TYPES.WEEKLY
            ? `Week of ${selectedDate}`
            : `${selectedHalf === "first" ? "1-15" : "16-end"} ${new Date(selectedDate).toLocaleDateString("en-US", { month: "long", year: "numeric" })}`,
        status: "active",
        totalPages,
        totalFarmers: selectedFarmerData.length,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.uid || "unknown",
      };

      // Save to Firestore - milk_journals collection
      const paperDocRef = doc(firestore, "milk_journals", uniqueId);
      await setDoc(paperDocRef, paperMetadata);

      // Collect all farmer mappings for batch write
      const mappingsToWrite = [];
      const mappingsRef = collection(paperDocRef, "farmer_mappings");

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdfDoc.addPage();
        }

        const startIdx = page * ROWS_PER_PAGE;
        const endIdx = Math.min(startIdx + ROWS_PER_PAGE, selectedFarmerData.length);
        const pageFarmers = selectedFarmerData.slice(startIdx, endIdx);

        // Header with unique ID
        pdfDoc.setFontSize(14);
        pdfDoc.setFont("helvetica", "bold");
        pdfDoc.text(`Paper ID: ${uniqueId}`, PDF_LAYOUT.TEXT_MARGIN_LEFT, PDF_LAYOUT.PAGE_MARGIN_TOP);
        pdfDoc.text(`Company: ${companyName}`, PDF_LAYOUT.TEXT_MARGIN_LEFT, 22);
        pdfDoc.text(`Page ${page + 1} of ${totalPages}`, PDF_LAYOUT.PAGE_INFO_X, PDF_LAYOUT.PAGE_MARGIN_TOP);

        // Paper type info
        pdfDoc.setFontSize(10);
        pdfDoc.setFont("helvetica", "normal");
        const typeLabel = paperType === PAPER_TYPES.SINGLE_DAY
          ? "Single Day"
          : paperType === PAPER_TYPES.WEEKLY
            ? "Weekly (Sat-Fri)"
            : "Half Monthly";
        pdfDoc.text(`Type: ${typeLabel}`, PDF_LAYOUT.TEXT_MARGIN_LEFT, 29);

        // Table headers
        const colWidths = {
          id: 12,
          name: 35, // Reduced from 50 to give more space for date columns
        };

        // Calculate date column width based on number of dates
        const dateColWidth = Math.max(PDF_LAYOUT.MIN_DATE_COL_WIDTH, Math.floor((PDF_LAYOUT.TOTAL_DATE_AREA_WIDTH - colWidths.id - colWidths.name) / dateHeaders.length));

        pdfDoc.setFillColor(0, 128, 128);
        pdfDoc.rect(PDF_LAYOUT.PAGE_MARGIN_LEFT, PDF_LAYOUT.TABLE_START_Y - 6, PDF_LAYOUT.TABLE_WIDTH, PDF_LAYOUT.TABLE_HEADER_HEIGHT, "F");

        pdfDoc.setTextColor(255, 255, 255);
        pdfDoc.setFontSize(9);
        pdfDoc.setFont("helvetica", "bold");

        let xPos = PDF_LAYOUT.TEXT_MARGIN_LEFT;
        pdfDoc.text("ID", xPos, PDF_LAYOUT.TABLE_START_Y);
        xPos += colWidths.id;
        pdfDoc.text("Farmer Name", xPos, PDF_LAYOUT.TABLE_START_Y);
        xPos += colWidths.name;

        for (let h = 0; h < dateHeaders.length; h++) {
          pdfDoc.text(dateHeaders[h].toString().substring(0, 6), xPos, PDF_LAYOUT.TABLE_START_Y);
          xPos += dateColWidth;
        }

        // Table rows
        pdfDoc.setTextColor(0, 0, 0);
        pdfDoc.setFont("helvetica", "normal");
        pdfDoc.setFontSize(8);

        let rowY = PDF_LAYOUT.TABLE_START_Y + 8;

        for (let i = 0; i < pageFarmers.length; i++) {
          const farmer = pageFarmers[i];
          const rowId = startIdx + i + 1; // 1-based row ID

          // Alternate row colors
          if (i % 2 === 0) {
            pdfDoc.setFillColor(240, 240, 240);
            pdfDoc.rect(PDF_LAYOUT.PAGE_MARGIN_LEFT, rowY - 4, PDF_LAYOUT.TABLE_WIDTH, PDF_LAYOUT.ROW_HEIGHT, "F");
          }

          // Draw row
          let cellX = PDF_LAYOUT.TEXT_MARGIN_LEFT;
          pdfDoc.text(rowId.toString(), cellX, rowY);
          cellX += colWidths.id;
          pdfDoc.text(farmer.cloudName.substring(0, 18), cellX, rowY); // Reduced from 25 to fit narrower column
          cellX += colWidths.name;

          // Empty cells for dates
          for (let d = 0; d < dateHeaders.length; d++) {
            pdfDoc.rect(cellX - 2, rowY - 4, dateColWidth - 2, PDF_LAYOUT.ROW_HEIGHT);
            cellX += dateColWidth;
          }

          rowY += PDF_LAYOUT.ROW_HEIGHT;

          // Collect farmer mapping for batch write
          mappingsToWrite.push({
            rowId,
            pageNumber: page + 1,
            otherName: farmer.otherName,
            cloudName: farmer.cloudName,
            farmerId: farmer.id,
          });
        }

        // Draw table border
        pdfDoc.setDrawColor(0, 128, 128);
        pdfDoc.rect(PDF_LAYOUT.PAGE_MARGIN_LEFT, PDF_LAYOUT.TABLE_START_Y - 6, PDF_LAYOUT.TABLE_WIDTH, rowY - PDF_LAYOUT.TABLE_START_Y + 10);

        // Footer
        pdfDoc.setFontSize(8);
        pdfDoc.setTextColor(128, 128, 128);
        pdfDoc.text(
          `Generated on ${new Date().toLocaleString()} | NeuraCraft Milk Journal System`,
          PDF_LAYOUT.TEXT_MARGIN_LEFT,
          PDF_LAYOUT.FOOTER_Y
        );
      }

      // Batch write farmer mappings to Firestore
      const BATCH_SIZE = 500; // Firestore batch limit
      for (let i = 0; i < mappingsToWrite.length; i += BATCH_SIZE) {
        const batch = writeBatch(firestore);
        const batchMappings = mappingsToWrite.slice(i, i + BATCH_SIZE);

        batchMappings.forEach((mapping, idx) => {
          const mappingDocRef = doc(mappingsRef, `mapping_${i + idx}`);
          batch.set(mappingDocRef, mapping);
        });

        await batch.commit();
      }

      // Save PDF
      pdfDoc.save(`milk_journal_${uniqueId}.pdf`);
      toast.success(`PDF generated! Paper ID: ${uniqueId}`);

    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Access Control Check */}
      {!canGenerate ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-red-700 mb-2">Access Restricted</h3>
          <p className="text-red-600">
            Only Company Admins and Managers can generate milk journal papers.
          </p>
          <p className="text-sm text-red-500 mt-2">
            Your current role: {userRole || "Unknown"}
          </p>
        </div>
      ) : (
        <>
          {/* Paper Type Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Select Paper Type</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setPaperType(PAPER_TYPES.SINGLE_DAY)}
                className={`p-4 rounded-lg border-2 transition ${
                  paperType === PAPER_TYPES.SINGLE_DAY
                    ? "border-cyan-600 bg-cyan-50"
                    : "border-gray-200 hover:border-cyan-300"
                }`}
              >
                <h3 className="font-semibold text-gray-800 mb-2">Single Day</h3>
                <p className="text-sm text-gray-600">Record milk for one day</p>
              </button>
              <button
                onClick={() => setPaperType(PAPER_TYPES.WEEKLY)}
                className={`p-4 rounded-lg border-2 transition ${
                  paperType === PAPER_TYPES.WEEKLY
                    ? "border-cyan-600 bg-cyan-50"
                    : "border-gray-200 hover:border-cyan-300"
                }`}
              >
                <h3 className="font-semibold text-gray-800 mb-2">Weekly</h3>
                <p className="text-sm text-gray-600">Saturday to Friday (7 days)</p>
              </button>
              <button
                onClick={() => setPaperType(PAPER_TYPES.HALF_MONTHLY)}
                className={`p-4 rounded-lg border-2 transition ${
                  paperType === PAPER_TYPES.HALF_MONTHLY
                    ? "border-cyan-600 bg-cyan-50"
                    : "border-gray-200 hover:border-cyan-300"
                }`}
              >
                <h3 className="font-semibold text-gray-800 mb-2">Half Monthly</h3>
                <p className="text-sm text-gray-600">15 days (1-15 or 16-end)</p>
              </button>
            </div>
          </div>

          {/* Date Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Select Date Range</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {paperType === PAPER_TYPES.SINGLE_DAY && "Select Date"}
                  {paperType === PAPER_TYPES.WEEKLY && "Select Week Start Date (Saturday)"}
                  {paperType === PAPER_TYPES.HALF_MONTHLY && "Select Month"}
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {paperType === PAPER_TYPES.HALF_MONTHLY && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Half of Month
                  </label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setSelectedHalf("first")}
                      className={`px-4 py-2 rounded-lg border transition ${
                        selectedHalf === "first"
                          ? "border-cyan-600 bg-cyan-50 text-cyan-700"
                          : "border-gray-300 hover:border-cyan-300"
                      }`}
                    >
                      First Half (1-15)
                    </button>
                    <button
                      onClick={() => setSelectedHalf("second")}
                      className={`px-4 py-2 rounded-lg border transition ${
                        selectedHalf === "second"
                          ? "border-cyan-600 bg-cyan-50 text-cyan-700"
                          : "border-gray-300 hover:border-cyan-300"
                      }`}
                    >
                      Second Half (16-end)
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Farmer Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <Hash size={20} />
                Enter Paper ID to Reuse Farmers
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Paste the Paper ID generated earlier to automatically select the same farmers (and order) for a new paper.
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder={`Enter the ${PAPER_ID_LENGTH}-digit paper ID...`}
                  value={paperIdLookup}
                  onChange={(e) => setPaperIdLookup(e.target.value)}
                  maxLength={PAPER_ID_LENGTH}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
                <button
                  onClick={loadFarmersFromPaper}
                  disabled={paperLookupLoading || !paperIdLookup.trim()}
                  className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                    paperLookupLoading || !paperIdLookup.trim()
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-cyan-600 text-white hover:bg-cyan-700"
                  }`}
                >
                  {paperLookupLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Loading...
                    </>
                  ) : (
                    "Reuse"
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Only papers from this company are accepted. Farmer selection is restored in the original order.
              </p>
            </div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Users size={24} />
                Select Farmers ({selectedFarmers.length} selected)
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={selectAllFarmers}
                  className="px-3 py-1 text-sm bg-cyan-100 text-cyan-700 rounded hover:bg-cyan-200 transition"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllFarmers}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                >
                  Deselect All
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search farmers by name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Farmer Table */}
            <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-lg">
              <table className="min-w-full bg-white">
                <thead className="bg-cyan-600 text-white sticky top-0">
                  <tr>
                    <th className="py-3 px-4 text-left">Select</th>
                    <th className="py-3 px-4 text-left">Farmer Name</th>
                    <th className="py-3 px-4 text-left">Address</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFarmers.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-6 text-gray-500">
                        No farmers found
                      </td>
                    </tr>
                  ) : (
                    filteredFarmers.map((farmer, index) => (
                      <tr
                        key={farmer.id}
                        className={`border-t cursor-pointer hover:bg-cyan-50 transition ${
                          selectedFarmers.includes(farmer.id) ? "bg-cyan-100" : index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                        onClick={() => toggleFarmerSelection(farmer.id)}
                      >
                        <td className="py-3 px-4">
                          {selectedFarmers.includes(farmer.id) ? (
                            <CheckSquare className="text-cyan-600" size={20} />
                          ) : (
                            <Square className="text-gray-400" size={20} />
                          )}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-800">
                          {farmer.cloudName}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {farmer.address}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Info box */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                💡 <strong>Note:</strong> Each page will contain up to {ROWS_PER_PAGE} farmers.
                Selected farmers: {selectedFarmers.length} ({Math.ceil(selectedFarmers.length / ROWS_PER_PAGE)} pages will be generated)
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            {/* CSV Export button hidden to protect farmer unique identifiers (otherName) */}
            <button
              onClick={generatePDF}
              disabled={selectedFarmers.length === 0 || generating}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                selectedFarmers.length === 0 || generating
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-cyan-600 text-white hover:bg-cyan-700"
              }`}
            >
              <Printer size={18} />
              {generating ? "Generating..." : "Generate PDF"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default GeneratePapersTab;
